import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@mui/material";
import { FC, useEffect } from "react";
import { Control, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";

import { ErrorsDisplayContainer, InputPasswordMui } from "@ryde/controls";
import {
  useApplication,
  useAuthActions,
  useRecaptchaToken,
  useSimpleNavigation,
} from "@ryde/redux";
import {
  errors,
  IResetPasswordModel,
  isLoggedInAtom,
  Log,
  loginErrorsAtom,
  RecaptchaEnterpriseActions,
} from "@ryde/shared";

import { RecaptchaEnterprise } from "../login/parts";
import { useYupResetPasswordValidationSchema } from "./parts";
import {
  buttonResetPasswordSx,
  ResetPasswordFormStyled,
} from "./reset-password.page.styled";

export const ResetPasswordPageForm: FC = () => {
  const { isAdminApp } = useApplication();
  const { handleResetPassword, setLoginErrors } = useAuthActions();
  const { t } = useTranslation();
  const isLoggedIn = useRecoilValue(isLoggedInAtom);
  const serverErrors = useRecoilValue(loginErrorsAtom);
  const [searchParams] = useSearchParams();
  const { getRecaptchaToken } = useRecaptchaToken();

  const { navigateHome, navigateLogin } = useSimpleNavigation();

  const resetToken = searchParams.get("token")?.trim() || "";

  const { yupResetPasswordValidationSchema } =
    useYupResetPasswordValidationSchema();
  const methods = useForm<IResetPasswordModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      password: "",
      repeatPassword: "",
    },
    resolver: yupResolver(yupResetPasswordValidationSchema),
  });

  const {
    control,
    getValues,
    clearErrors,
    trigger,
    formState: { isValid },
  } = methods;

  useEffect(() => {
    if (isLoggedIn) {
      clearErrors();
      navigateHome();
    }
  }, [clearErrors, isLoggedIn, navigateHome]);

  useEffect(() => {
    if (!resetToken) {
      // Do not call API without a token — avoids opaque 422 validation errors
      setLoginErrors([t(errors.auth.passwordResetLinkMissing)]);
    }
  }, [resetToken, setLoginErrors, t]);

  const handleSubmit = async (): Promise<void> => {
    if (!resetToken) {
      setLoginErrors([t(errors.auth.passwordResetLinkMissing)]);
      return;
    }

    if (!window?.grecaptcha?.enterprise) {
      Log.error(t("errors.recaptchaNotLoaded"));
      return;
    }

    try {
      await trigger();

      if (!isValid) {
        return;
      }

      const { password } = getValues();

      const recaptchaToken = await getRecaptchaToken(
        RecaptchaEnterpriseActions.RESET_PASSWORD,
      );

      if (!recaptchaToken) {
        return;
      }

      const result = await handleResetPassword({
        token: resetToken,
        password,
        recaptchaToken,
      });

      if (result.success) {
        navigateLogin();
      }
    } catch (error) {
      const errorName = t("errors.resetPasswordError");
      Log.error(errorName, error);
      setLoginErrors([`${errorName}: ${(error as Error)?.message}`]);
    }
  };

  return (
    <FormProvider {...methods}>
      <RecaptchaEnterprise />
      <ResetPasswordFormStyled data-e2e-mode={isAdminApp ? "admin" : "webapp"}>
        <ErrorsDisplayContainer
          serverErrors={serverErrors}
          marginLeft={"0px"}
        />
        <div className="authArea">
          <div className="labelSignIn">{t("ui.RESET_PASSWORD")}</div>
          <div className="fieldContainer">
            <div className="userTextLabels">
              <span>
                {t("ui.PASSWORD")} <i>*</i>
              </span>
            </div>
            <InputPasswordMui
              control={control as unknown as Control}
              name="password"
              formLabel={t("ui.PASSWORD")}
              e2eData={"password_reset_input"}
            />
          </div>
          <div className="fieldContainer">
            <div className="userTextLabels">
              <span>
                {t("ui.REPEAT_PASSWORD")} <i>*</i>
              </span>
            </div>
            <InputPasswordMui
              control={control as unknown as Control}
              name="repeatPassword"
              formLabel={t("ui.REPEAT_PASSWORD")}
              e2eData={"repeat_password_reset_input"}
            />
          </div>
          <Button
            data-e2e="button-reset-password"
            variant="contained"
            color="primary"
            sx={buttonResetPasswordSx}
            onClick={handleSubmit}
            disabled={!resetToken}
          >
            {t("ui.RESET_PASSWORD")}
          </Button>
        </div>
      </ResetPasswordFormStyled>
    </FormProvider>
  );
};
